import { PERMISSION_GROUPS, type PermissionKey, type UserListItemDto } from './contractsMirror.js';

/**
 * The escape hatch: all 35 permissions for ONE person, grouped, each a switch.
 *
 * This used to be the front door (as a 19-column × N-user matrix, then as a per-person
 * list below `md`). It is now reachable only from the Advanced row on the role screen,
 * because production says overrides are a 1-in-50 event, not the normal way access is
 * granted. Nothing was removed: every key the matrix exposed is here, and a toggle takes
 * the identical confirm → PATCH path, so there is no second write surface to drift.
 *
 * Two things this deliberately does NOT do:
 *   - no dirty state and no Save button. Each toggle confirms and writes on its own, so a
 *     half-applied privilege change cannot be sitting unsaved on screen.
 *   - no local source of truth. `isOn` reads the server's resolved state (with an optimistic
 *     layer the parent clears on settle), so what is drawn is what the server agreed to.
 */

interface AdvancedAccessPanelProps {
  user: UserListItemDto;
  isOn: (user: UserListItemDto, permission: PermissionKey) => boolean;
  onToggle: (user: UserListItemDto, permission: PermissionKey) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

export function AdvancedAccessPanel({ user, isOn, onToggle, t }: AdvancedAccessPanelProps) {
  return (
    <div className="overflow-hidden rounded-md border border-border bg-card">
      {PERMISSION_GROUPS.map((group) => (
        <section key={group.label}>
          <h3 className="bg-muted/40 px-3 py-2 font-semibold text-foreground text-xs uppercase tracking-wide">
            {t(`users:permissions.groups.${group.label}`)}
          </h3>
          <ul className="divide-y divide-border/60">
            {group.permissions.map((permission) => {
              const on = isOn(user, permission);
              const overridden =
                user.overrides[permission] === 'allow' || user.overrides[permission] === 'deny';
              return (
                <li key={permission} className="flex items-center gap-3 px-3 py-3">
                  {/* `min-w-0` lets a long Spanish permission name wrap instead of
                      squeezing the switch below its 24px target. */}
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium text-foreground text-sm">
                      {t(`users:permissions.keys.${permission}`)}
                    </span>
                    {overridden ? (
                      // Says WHY this row differs from the role. Text, not a colour cue.
                      <span className="mt-0.5 block text-muted-foreground text-xs">
                        {t('users:access.overriddenNote')}
                      </span>
                    ) : null}
                  </span>
                  <PermissionToggle
                    user={user}
                    permission={permission}
                    on={on}
                    onToggle={onToggle}
                    t={t}
                  />
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

/**
 * The grant/revoke switch for one (user, permission) pair. 24px tall, the WCAG 2.5.8
 * target floor.
 */
function PermissionToggle({
  user,
  permission,
  on,
  onToggle,
  t,
}: {
  user: UserListItemDto;
  permission: PermissionKey;
  on: boolean;
  onToggle: (user: UserListItemDto, permission: PermissionKey) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={t('users:permissions.toggleLabel', {
        permission: t(`users:permissions.keys.${permission}`),
        name: user.name,
      })}
      onClick={() => onToggle(user, permission)}
      className={`inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
        on ? 'border-transparent bg-primary' : 'border-border bg-muted'
      }`}
    >
      <span
        aria-hidden="true"
        className={`inline-block size-5 rounded-full bg-card shadow transition-transform ${
          on ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}
